/**
 * HOGV accessibility: keep keyboard focus out of carousel slides that are aria-hidden.
 *
 * Horizon marks off-screen slides `aria-hidden="true"` but their product/blog links stay
 * focusable, so keyboard and screen-reader users can tab into content that is announced as
 * hidden (Lighthouse "aria-hidden-focus"). This only toggles tabindex on those links: taps and
 * clicks are unaffected, and the original tabindex is restored when the slide becomes visible.
 */
(() => {
  const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
  const STORE = 'hogvTabindex';

  function sync(slide) {
    const hidden = slide.getAttribute('aria-hidden') === 'true';
    slide.querySelectorAll(FOCUSABLE).forEach((el) => {
      if (hidden) {
        if (!(STORE in el.dataset)) el.dataset[STORE] = el.getAttribute('tabindex') ?? '';
        el.setAttribute('tabindex', '-1');
      } else if (STORE in el.dataset) {
        const original = el.dataset[STORE];
        if (original === '') el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', original);
        delete el.dataset[STORE];
      }
    });
  }

  function syncAll(root = document) {
    root.querySelectorAll('slideshow-slide[aria-hidden]').forEach(sync);
  }

  syncAll();

  new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes') {
        if (m.target.localName === 'slideshow-slide') sync(m.target);
      } else {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.localName === 'slideshow-slide') sync(node);
          else syncAll(node);
        });
      }
    }
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-hidden'] });
})();
